import React from 'react';
import { Card, Row, Col } from 'antd';
import { Link } from 'react-router-dom';
import cx from 'classnames';

import { getFullName } from 'utils/personUtils';
import { getLocation } from 'utils/locationUtils';
import BaseLabel from 'components/wrappers/BaseLabel';
import { getClientStatusName } from 'utils/clientUtils';
import {
  CustomerInfoResponseModel,
  CustomerStatusEnum,
} from 'api/backofficeApiClient';
import { isAdminSelector } from 'redux/selectors/authenticationSelectors';
import { useAppSelector } from 'hooks/useAppSelector';

type Props = {
  className?: string;
  enableStatus?: boolean;
  client: CustomerInfoResponseModel;
};

export default function ClientCard(props: Props) {
  const { client, enableStatus = true, className = '' } = props;
  const isAdminRole = useAppSelector(isAdminSelector);
  const {
    customerId,
    customerKey,
    recruiterCompanyKey,
    status,
    customerInfo,
    recruiterCompanyName,
    primaryContacts,
  } = client;
  const {
    companyName,
    additionalInvoiceName,
    location: {
      countryName,
      address,
      city,
      internationalCityName,
      state,
      internationalStateName,
      zip,
    },
  } = customerInfo;

  const customerLocation = getLocation(
    countryName,
    address,
    state || internationalStateName,
    city || internationalCityName,
    zip,
  );

  // Display only first primary contacts section for now
  const firstPrimaryContacts = primaryContacts && primaryContacts[0];
  const primaryContactName = getFullName(
    firstPrimaryContacts?.firstName,
    firstPrimaryContacts?.lastName,
  );

  return (
    <Card className={cx('card-item', className)}>
      <div className="card-inner">
        <div className="inner-text-box">
          <Link
            className="card-title"
            to={`/customer/${
              client.status === CustomerStatusEnum.Draft
                ? customerId
                : customerKey
            }`}
          >
            {companyName}
          </Link>
        </div>
      </div>

      <Row gutter={[20, 10]} className="titled-sheet">
        {customerKey && (
          <Col xs={24} md={12}>
            <div className="title">Customer ID: </div>
            <div>{customerKey}</div>
          </Col>
        )}

        {recruiterCompanyName && (
          <Col xs={24} md={12}>
            <div className="title">Member company: </div>
            {isAdminRole ? (
              <Link to={`/member/${recruiterCompanyKey}`}>
                {recruiterCompanyName}
              </Link>
            ) : (
              <div>{recruiterCompanyName}</div>
            )}
          </Col>
        )}

        {additionalInvoiceName && (
          <Col xs={24} md={12}>
            <div className="title">Invoice name (Legal/DBA): </div>
            <div>{additionalInvoiceName}</div>
          </Col>
        )}

        {!!customerLocation && (
          <Col xs={24} md={12}>
            <div className="title">Location: </div>
            <div>{customerLocation}</div>
          </Col>
        )}

        {!!primaryContactName && (
          <Col xs={24} md={12}>
            <div className="title">Primary contact: </div>
            <div>{primaryContactName}</div>
          </Col>
        )}

        {firstPrimaryContacts?.title && (
          <Col xs={24} md={12}>
            <div className="title">PC title: </div>
            <div>{firstPrimaryContacts.title}</div>
          </Col>
        )}

        {firstPrimaryContacts?.email && (
          <Col xs={24} md={12}>
            <div className="title">PC email: </div>
            <div>{firstPrimaryContacts.email}</div>
          </Col>
        )}

        {firstPrimaryContacts?.phoneNumber && (
          <Col xs={24} md={12}>
            <div className="title">PC phone number: </div>
            <div>{firstPrimaryContacts?.phoneNumber}</div>
          </Col>
        )}
      </Row>

      {enableStatus && (
        <div className="label-wrapper">
          <BaseLabel status={getClientStatusName(status)} />
        </div>
      )}
    </Card>
  );
}
